import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/store';
import { updateUser as updateUserInStore } from '@/store/slices/authSlice';
import { useAuth } from '@/hooks/useAuth';
import { useUpdateLocalePreferencesMutation } from '@/store/api/apiSlice';
import { normalizeAppLocale, applyDocumentLanguage } from '@/i18n/languages';
import { ensureLocaleLoaded } from '@/i18n/loadLocale';

export function useLocaleChange() {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const [updateLocalePreferences] = useUpdateLocalePreferencesMutation();

  const currentLocale = normalizeAppLocale(i18n.language);

  const changeLocale = useCallback(
    async (nextLocale: string) => {
      const code = normalizeAppLocale(nextLocale);
      if (code === currentLocale) return;

      localStorage.setItem('co_taskr_locale', code);
      await ensureLocaleLoaded(code);
      await i18n.changeLanguage(code);
      applyDocumentLanguage(code);

      if (user) {
        try {
          await updateLocalePreferences({ locale: code }).unwrap();
          dispatch(updateUserInStore({ ...user, locale: code }));
        } catch {
          /* UI language still switched locally */
        }
      }
    },
    [currentLocale, dispatch, i18n, updateLocalePreferences, user],
  );

  return { currentLocale, changeLocale };
}
